import React, { useState, useEffect, useRef, useMemo, useContext } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import ContentBox from "../components/ContentBox";
import ValuesBox from "../components/ValuesBox";
import CartModal, { loadCart, persistCart } from "../components/CartModal";
import CurrencyBreakdown from "../components/CurrencyBreakdown";
import { FontScaleContext } from "../App";

function SearchLoading() {
    return (
        <div className="flex justify-center items-center py-12">
            <p className="text-white text-lg">Searching...</p>
        </div>
    );
}

function SearchEmpty() {
    return (
        <div className="text-center py-8">
            <p className="text-white">No results found</p>
        </div>
    );
}

// Helper function to generate slug from furniture name
const generateSlug = (name) => {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '')
        .trim();
};

// Helper function to format relative time
const formatRelativeTime = (dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffYears > 0) {
        return `${diffYears} ${diffYears === 1 ? 'year' : 'years'} ago`;
    } else if (diffMonths > 0) {
        return `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
    } else if (diffWeeks > 0) {
        return `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`;
    } else if (diffDays > 0) {
        return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else if (diffHours > 0) {
        return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffMins > 0) {
        return `${diffMins} ${diffMins === 1 ? 'min' : 'mins'} ago`;
    } else {
        return 'Just now';
    }
};

function ListViewRow({ item, index, listGridCols, hideUpdated = false, onAddToCart, cartQty = 0, onUpdateCartQty = null }) {
    const fontScale = useContext(FontScaleContext);
    const [smallMissing, setSmallMissing] = useState(false);
    const [activeBreakdown, setActiveBreakdown] = useState(null); // 'min' | 'max' | 'avg'

    const inCart = cartQty > 0;
    const qty = inCart ? cartQty : 1;

    const placeholderSmall = `${process.env.PUBLIC_URL}/img/Placeholder_s.png`;
    const displaySmallImg = item.smallImg || placeholderSmall;

    // Shrink desktop padding/gap as font scale grows to prevent overflow
    const deskPad = fontScale > 1.3 ? 'p-1' : fontScale > 1.1 ? 'p-2' : 'p-3';
    const deskGap = fontScale > 1.3 ? 'gap-1' : fontScale > 1.1 ? 'gap-2' : 'gap-3';

    const rawMin = Math.floor(Number(item.minValue) || 0);
    const rawAvg = Math.floor(Number(item.avgValue) || 0);
    const rawMax = Math.floor(Number(item.maxValue) || 0);

    const displayMin = rawMin * qty;
    const displayAvg = rawAvg * qty;
    const displayMax = rawMax * qty;

    const rowBgClass = index % 2 === 0 ? 'bg-gray-50' : 'bg-white';

    const addToCart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onAddToCart) {
            onAddToCart({
                id: item.id,
                name: item.name,
                iconImg: item.iconImg,
                minHabloon: rawMin,
                avgHabloon: rawAvg,
                maxHabloon: rawMax,
                qty: 1,
            });
            toast.success(`${item.name} added to cart`, { autoClose: 2000 });
        }
    };

    const adjustCartQty = (e, delta) => {
        e.preventDefault();
        e.stopPropagation();
        if (onUpdateCartQty) onUpdateCartQty(item.id, cartQty + delta);
    };

    const cartControl = (
        <div onClick={e => { e.preventDefault(); e.stopPropagation(); }}>
            {!inCart ? (
                <button
                    onClick={addToCart}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="w-6 h-6 rounded-full bg-maroon hover:bg-maroon/90 active:bg-maroon/80 flex items-center justify-center text-white font-bold text-base leading-none transition-colors shrink-0"
                    title="Add to Cart"
                    style={{ boxShadow: '0 2px 0 rgba(0,0,0,0.18)' }}
                >+</button>
            ) : (
                <div
                    className="flex items-center bg-maroon/10 rounded-full px-1 border border-maroon/20"
                    style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.06)' }}
                >
                    <button
                        onClick={(e) => adjustCartQty(e, -1)}
                        onPointerDown={(e) => e.stopPropagation()}
                        className="w-4 h-4 flex items-center justify-center text-maroon hover:text-maroon/70 font-bold text-sm leading-none transition-colors"
                    >−</button>
                    <span className="text-[12px] font-bold text-maroon w-5 text-center tabular-nums">{cartQty}</span>
                    <button
                        onClick={(e) => adjustCartQty(e, 1)}
                        onPointerDown={(e) => e.stopPropagation()}
                        className="w-4 h-4 flex items-center justify-center text-maroon hover:text-maroon/70 font-bold text-sm leading-none transition-colors"
                    >+</button>
                </div>
            )}
        </div>
    );

    const toggleBreakdown = (e, key) => {
        e.preventDefault();
        e.stopPropagation();
        setActiveBreakdown(prev => prev === key ? null : key);
    };

    // Renders a tappable value cell for the list desktop view
    const renderListValueCell = (value, key) => {
        const isActive = activeBreakdown === key;
        if (!value) return <span className="text-gray-300 text-[8px]">&#9679;</span>;
        return (
            <div
                className="flex flex-col items-center justify-center gap-1 cursor-pointer select-none min-h-[32px]"
                onMouseEnter={() => setActiveBreakdown(key)}
                onMouseLeave={() => setActiveBreakdown(null)}
                onClick={(e) => toggleBreakdown(e, key)}
                title="Click or hover for breakdown"
            >
                {isActive ? (
                    <CurrencyBreakdown value={value} size="sm" />
                ) : (
                    <div className="flex items-center gap-1">
                        <img src="/img/habloon_fullsize.png" alt="" className="w-4 h-4" style={{ imageRendering: 'pixelated' }} />
                        <span className={`text-sm ${key === 'avg' ? 'font-bold' : ''} text-gray-900 tabular-nums`}>{value}</span>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Link
            to={`/furniture/${generateSlug(item.name)}`}
            className={`block border-b border-gray-400/30 transition-colors ${rowBgClass}`}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5eaea'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
        >
            {/* Mobile layout */}
            <div className="flex items-center gap-2 p-2.5 md:hidden">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center overflow-hidden">
                    {smallMissing ? (
                        <span className="text-xs text-gray-500">No img</span>
                    ) : (
                        <img src={displaySmallImg} onError={() => setSmallMissing(true)} alt={item.name} loading="eager" style={{ imageRendering: "pixelated", width: "auto", height: "auto", maxWidth: "none", maxHeight: "none" }} />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-['Jersey_10'] text-[15px] uppercase tracking-wide text-gray-900 truncate">
                        {item.name}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className={`inline-flex items-center gap-0.5 text-xs ${item.originalPrice ? 'text-gray-500' : 'text-gray-300'}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                            {item.originalPrice || '0'}
                        </span>
                        {item.minValue && (
                            <span className="inline-flex items-center gap-0.5 text-xs text-gray-500">
                                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
                                {displayMin}H
                            </span>
                        )}
                        {item.maxValue && (
                            <span className="inline-flex items-center gap-0.5 text-xs text-gray-500">
                                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
                                {displayMax}H
                            </span>
                        )}
                    </div>
                </div>
                {/* Mobile avg + qty + cart */}
                <div className="flex-shrink-0 flex flex-col items-end gap-1">
                    <div
                        className="cursor-pointer"
                        onClick={(e) => toggleBreakdown(e, 'avg')}
                    >
                        {activeBreakdown === 'avg' ? (
                            <CurrencyBreakdown value={displayAvg} size="sm" />
                        ) : (
                            <>
                                <div className="text-sm font-bold text-gray-900 tabular-nums text-right">{item.avgValue ? `${displayAvg}H` : 'N/A'}</div>
                                <div className="text-[10px] text-gray-400 uppercase tracking-wide text-right">avg</div>
                            </>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        {onAddToCart && cartControl}
                    </div>
                </div>
            </div>

            {/* Desktop layout */}
            <div className={`hidden md:grid items-center ${deskPad} ${deskGap} min-h-[3.5rem] pr-4`} style={{ gridTemplateColumns: listGridCols }}>
                <div className="flex items-center justify-center w-full h-full overflow-hidden">
                    {smallMissing ? (
                        <span className="text-xs text-gray-500">No img</span>
                    ) : (
                        <img src={displaySmallImg} onError={() => setSmallMissing(true)} alt={item.name} loading="eager" style={{ imageRendering: "pixelated", width: "auto", height: "auto", maxWidth: "none", maxHeight: "none" }} />
                    )}
                </div>
                <div className="min-w-0 overflow-hidden">
                    <div className="font-['Jersey_10'] text-[16px] uppercase tracking-wide text-gray-900 truncate">
                        {item.name}
                    </div>
                    {item.description && (
                        <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                            {item.description}
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-center gap-1">
                    {item.originalPrice ? (
                        <>
                            <span className="text-sm text-gray-900">{item.originalPrice}</span>
                            <img src="/img/values.gif" alt="credits" className="w-4 h-4" />
                        </>
                    ) : (
                        <span className="text-gray-300 text-[8px]">&#9679;</span>
                    )}
                </div>
                <div className="flex items-center justify-center">
                    {renderListValueCell(displayMin, 'min')}
                </div>
                <div className="flex items-center justify-center">
                    {renderListValueCell(displayMax, 'max')}
                </div>
                <div className="flex items-center justify-center">
                    {renderListValueCell(displayAvg, 'avg')}
                </div>
                {!hideUpdated && <div className="text-xs text-gray-600 text-center">{formatRelativeTime(item.lastUpdated)}</div>}
                {/* Cart control */}
                <div className="flex items-center justify-center">
                    {onAddToCart && cartControl}
                </div>
            </div>
        </Link>
    );
}

export default function Values() {
    const fontScale = useContext(FontScaleContext);
    const iconCol = `${Math.round(100 / fontScale)}px`;
    const valCol = `${Math.round(100 / fontScale)}px`;
    const lastCol = `${Math.round(120 / fontScale)}px`;
    const cartCol = `${Math.round(60 / fontScale)}px`;
    const hideUpdated = fontScale >= 1.4;
    const listGridCols = hideUpdated
        ? `${iconCol} 3fr ${valCol} ${valCol} ${valCol} ${valCol} ${cartCol}`
        : `${iconCol} 3fr ${valCol} ${valCol} ${valCol} ${valCol} ${lastCol} ${cartCol}`;
    const listHeaderPad = fontScale > 1.3 ? 'p-1' : fontScale > 1.1 ? 'p-2' : 'p-3';
    const listHeaderGap = fontScale > 1.3 ? 'gap-1' : fontScale > 1.1 ? 'gap-2' : 'gap-3';

    const [queryInput, setQueryInput] = useState(() => sessionStorage.getItem('values_queryInput') || "");
    const [query, setQuery] = useState(() => sessionStorage.getItem('values_query') || "");
    const [isSearching, setIsSearching] = useState(false);
    const [allValues, setAllValues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(() => parseInt(sessionStorage.getItem('values_page') || '1', 10));
    const [totalPages, setTotalPages] = useState(1);
    const [categories, setCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(() => {
        const saved = sessionStorage.getItem('values_categoryId');
        return saved !== null ? parseInt(saved, 10) : null;
    });
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [viewMode, setViewMode] = useState(() => sessionStorage.getItem('values_viewMode') || "grid");
    const [sortBy, setSortBy] = useState(() => sessionStorage.getItem('values_sortBy') || "newest");
    const [showCategoriesMobile, setShowCategoriesMobile] = useState(false);

    // Cart
    const [cart, setCart] = useState(() => loadCart());
    const [showCart, setShowCart] = useState(false);

    const handleAddToCart = ({ id, name, iconImg, minHabloon, avgHabloon, maxHabloon, qty }) => {
        setCart(prev => {
            const existing = prev.find(i => i.id === id);
            const newCart = existing
                ? prev.map(i => i.id === id ? { ...i, qty: i.qty + qty } : i)
                : [...prev, { id, name, iconImg, minHabloon, avgHabloon, maxHabloon, qty }];
            persistCart(newCart);
            return newCart;
        });
    };

    const handleRemoveFromCart = (id) => {
        setCart(prev => {
            const newCart = prev.filter(i => i.id !== id);
            persistCart(newCart);
            return newCart;
        });
    };

    const handleUpdateCartQty = (id, qty) => {
        setCart(prev => {
            const newCart = prev.map(i => i.id === id ? { ...i, qty } : i);
            persistCart(newCart);
            return newCart;
        });
    };

    const handleClearCart = () => {
        setCart([]);
        persistCart([]);
    };

    // Unified qty update — removes at 0
    const handleCartQtyChange = (id, qty) => {
        if (qty <= 0) {
            handleRemoveFromCart(id);
        } else {
            handleUpdateCartQty(id, qty);
        }
    };

    const debounceTimerRef = useRef(null);
    const isInitialMount = useRef(true);

    useEffect(() => { sessionStorage.setItem('values_queryInput', queryInput); }, [queryInput]);
    useEffect(() => { sessionStorage.setItem('values_query', query); }, [query]);
    useEffect(() => { sessionStorage.setItem('values_sortBy', sortBy); }, [sortBy]);
    useEffect(() => { sessionStorage.setItem('values_viewMode', viewMode); }, [viewMode]);
    useEffect(() => { sessionStorage.setItem('values_page', currentPage); }, [currentPage]);
    useEffect(() => {
        if (selectedCategoryId === null) sessionStorage.removeItem('values_categoryId');
        else sessionStorage.setItem('values_categoryId', selectedCategoryId);
    }, [selectedCategoryId]);

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
        loadValues(currentPage);
    }, [currentPage, selectedCategoryId, query, sortBy]);

    const loadCategories = async () => {
        try {
            setCategoriesLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}categories/public`);
            const data = await response.json();

            if (data.success) {
                setCategories(data.categories);
            }
        } catch (error) {
            console.error('Error loading categories:', error);
        } finally {
            setCategoriesLoading(false);
        }
    };

    const loadValues = async (page) => {
        try {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            let url = `${apiUrl}values/all?page=${page}&sortBy=${sortBy}`;

            if (selectedCategoryId) {
                url += `&categoryId=${selectedCategoryId}`;
            }

            if (query.trim().length >= 3) {
                url += `&search=${encodeURIComponent(query)}`;
            }

            const response = await fetch(url);
            const data = await response.json();

            if (data.success) {
                setAllValues(data.values);
                setTotalPages(data.pagination.totalPages);
                setCurrentPage(page);
            }
        } catch (error) {
            console.error('Error loading values:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const q = queryInput.trim();

        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

        if (q.length < 3) {
            setIsSearching(false);
            setQuery("");
            setCurrentPage(1);
            return;
        }

        setIsSearching(true);

        debounceTimerRef.current = setTimeout(() => {
            setQuery(q);
            setCurrentPage(1);
            setIsSearching(false);
        }, 500);

        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, [queryInput]);

    const [columns, setColumns] = useState(3);

    useEffect(() => {
        const updateColumns = () => {
            setColumns(window.innerWidth >= 1300 ? 2 : 1);
        };

        updateColumns();
        window.addEventListener("resize", updateColumns);
        return () => window.removeEventListener("resize", updateColumns);
    }, []);

    const displayItems = useMemo(() => {
        return allValues;
    }, [allValues]);

    return (
        <div className="w-full mx-auto px-4 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-3">
                    <div className="lg:hidden mb-3">
                        <button
                            onClick={() => setShowCategoriesMobile(v => !v)}
                            className="w-full bg-coal text-white font-bold text-sm py-2 rounded flex items-center justify-center gap-2"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <line x1="3" y1="6" x2="21" y2="6"></line>
                                <line x1="3" y1="12" x2="21" y2="12"></line>
                                <line x1="3" y1="18" x2="21" y2="18"></line>
                            </svg>

                            <span>{showCategoriesMobile ? "Hide Categories" : "Show Categories"}</span>
                        </button>
                    </div>

                    <div className={`${showCategoriesMobile ? "block" : "hidden"} lg:block`}>
                        <ContentBox theme="default" tint="gold" header="Categories">
                            {categoriesLoading ? (
                                <p className="text-gray-700">Loading categories...</p>
                            ) : categories.length === 0 ? (
                                <p className="text-gray-700">No categories found.</p>
                            ) : (
                                <div className="space-y-2">
                                    <button
                                        onClick={() => {
                                            setSelectedCategoryId(null);
                                            setCurrentPage(1);
                                            setShowCategoriesMobile(false);
                                        }}
                                        className={`w-full flex items-center gap-1 p-3 rounded transition-all ${
                                            selectedCategoryId === null
                                                ? 'bg-maroon text-white'
                                                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                        }`}
                                    >
                                        <span className="font-['Jersey_10'] mt-0.5 uppercase tracking-[1px] text-sm">
                                            All Categories
                                        </span>
                                    </button>
                                    {categories.map((category) => (
                                        <button
                                            key={category.id}
                                            onClick={() => {
                                                setSelectedCategoryId(category.id);
                                                setCurrentPage(1);
                                                setShowCategoriesMobile(false);
                                            }}
                                            className={`w-full flex items-center gap-1 p-3 rounded transition-all ${
                                                selectedCategoryId === category.id
                                                    ? 'bg-maroon text-white'
                                                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                            }`}
                                        >
                                            {category.iconUrl && (
                                                <img
                                                    src={category.iconUrl}
                                                    alt={category.name}
                                                />
                                            )}
                                            <span className="font-['Jersey_10'] mt-0.5 uppercase tracking-[1px] text-sm">
                                                {category.name}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </ContentBox>
                    </div>
                </div>

                <div className="lg:col-span-9">
                    <ContentBox theme="default" tint="lightskyblue" header="Furni Values">
                        <div className="sticky top-0 z-20 bg-offwhite -mx-4 px-4 py-3 mb-3 border-b border-black/5 flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
                            {/* Search input with clear button */}
                            <div className="relative flex-1 min-w-0">
                                <input
                                    type="text"
                                    placeholder="Search furni name or class (min 3 chars)..."
                                    value={queryInput}
                                    onChange={(e) => setQueryInput(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                                />
                                {queryInput && (
                                    <button
                                        onClick={() => {
                                            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
                                            setQueryInput("");
                                            setQuery("");
                                            setCurrentPage(1);
                                        }}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                        title="Clear search"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                        </svg>
                                    </button>
                                )}
                            </div>

                            {/* Controls row: sort dropdown + view + cart */}
                            <div className="flex items-center gap-2">
                                <select
                                    value={sortBy}
                                    onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                                    className="flex-1 md:flex-none px-3 py-2 rounded border border-gray-300 bg-gray-200 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-maroon cursor-pointer"
                                    style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                                >
                                    <option value="newest">Newest Added</option>
                                    <option value="updated">Recently Updated</option>
                                    <option value="highest">Highest → Lowest</option>
                                    <option value="lowest">Lowest → Highest</option>
                                </select>

                                <div className="flex gap-2 shrink-0 overflow-visible">
                                    <button
                                        onClick={() => setViewMode("grid")}
                                        className={`p-2 rounded transition-all ${viewMode === "grid" ? 'bg-maroon text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                                        title="Grid View"
                                        style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                                            <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                                        </svg>
                                    </button>

                                    <button
                                        onClick={() => setViewMode("list")}
                                        className={`p-2 rounded transition-all ${viewMode === "list" ? 'bg-maroon text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                                        title="List View"
                                        style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
                                            <line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/>
                                            <line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                                        </svg>
                                    </button>

                                    <button
                                        onClick={() => setShowCart(true)}
                                        className="relative p-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all overflow-visible"
                                        title="View Cart"
                                        style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                                        </svg>
                                        {cart.length > 0 && (
                                            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-maroon text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                                                {cart.length > 9 ? '9+' : cart.length}
                                            </span>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <p className="text-white text-center">Loading values...</p>
                        ) : isSearching ? (
                            <SearchLoading />
                        ) : query.trim().length >= 3 && displayItems.length === 0 ? (
                            <SearchEmpty />
                        ) : (
                            <>
                                {viewMode === "grid" ? (
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: `repeat(${columns}, 1fr)`,
                                            gap: "1rem",
                                        }}
                                    >
                                        {displayItems.map((item) => (
                                            <Link key={item.id} to={`/furniture/${generateSlug(item.name)}`} className="block">
                                                <ValuesBox
                                                    itemId={item.id}
                                                    name={item.name}
                                                    description={item.description}
                                                    categoryName={item.categoryName}
                                                    originalPrice={item.originalPrice}
                                                    minHabloon={item.minValue}
                                                    avgHabloon={item.avgValue}
                                                    maxHabloon={item.maxValue}
                                                    largeImg={item.largeImg}
                                                    smallImg={item.smallImg}
                                                    iconImg={item.iconImg}
                                                    lastUpdated={item.lastUpdated}
                                                    onAddToCart={handleAddToCart}
                                                    cartQty={cart.find(i => i.id === item.id)?.qty || 0}
                                                    onUpdateCartQty={handleCartQtyChange}
                                                />
                                            </Link>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="overflow-hidden">
                                        {/* Mobile header */}
                                        <div className="flex items-center gap-3 px-2.5 py-2 bg-gray-500/20 border-b-2 border-gray-400/50 md:hidden">
                                            <div className="w-12 text-xs font-['Jersey_10'] uppercase tracking-wide text-gray-700 text-center">Img</div>
                                            <div className="flex-1 text-xs font-['Jersey_10'] uppercase tracking-wide text-gray-700">Rare</div>
                                            <div className="text-xs font-['Jersey_10'] uppercase tracking-wide text-gray-700">Avg</div>
                                        </div>

                                        {/* Desktop header */}
                                        <div className={`hidden md:grid items-center ${listHeaderPad} ${listHeaderGap} pr-4 bg-gray-500/20 border-b-2 border-gray-400/50`} style={{ gridTemplateColumns: listGridCols }}>
                                            <div className="flex items-center justify-center gap-1 text-xs font-['Jersey_10'] uppercase tracking-wide text-gray-900">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                                                Image
                                            </div>
                                            <div className="flex items-center gap-1 text-xs font-['Jersey_10'] uppercase tracking-wide text-gray-900">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                                                Rare
                                            </div>
                                            <div className="flex items-center justify-center gap-1 text-xs font-['Jersey_10'] uppercase tracking-wide text-gray-900">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                                                Release
                                            </div>
                                            <div className="flex items-center justify-center gap-1 text-xs font-['Jersey_10'] uppercase tracking-wide text-gray-900">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
                                                Min
                                            </div>
                                            <div className="flex items-center justify-center gap-1 text-xs font-['Jersey_10'] uppercase tracking-wide text-gray-900">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
                                                Max
                                            </div>
                                            <div className="flex items-center justify-center gap-1 text-xs font-['Jersey_10'] uppercase tracking-wide text-gray-900">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
                                                Avg
                                            </div>
                                            {!hideUpdated && (
                                                <div className="flex items-center justify-center gap-1 text-xs font-['Jersey_10'] uppercase tracking-wide text-gray-900">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                                    Updated
                                                </div>
                                            )}
                                            <div className="flex items-center justify-center gap-1 text-xs font-['Jersey_10'] uppercase tracking-wide text-gray-900">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
                                                Cart
                                            </div>
                                        </div>

                                        {/* Rows */}
                                        {displayItems.map((item, index) => (
                                            <ListViewRow key={item.id} item={item} index={index} listGridCols={listGridCols} hideUpdated={hideUpdated} onAddToCart={handleAddToCart} cartQty={cart.find(i => i.id === item.id)?.qty || 0} onUpdateCartQty={handleCartQtyChange} />
                                        ))}
                                    </div>
                                )}

                                {totalPages > 1 && (
                                    <div className="mt-8 flex items-center justify-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                            disabled={currentPage === 1}
                                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-all"
                                        >
                                            Previous
                                        </button>
                                        <span className="px-4 py-2 text-gray-900">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-all"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </ContentBox>
                </div>
            </div>

            {/* Cart Modal */}
            {showCart && (
                <CartModal
                    cart={cart}
                    onClose={() => setShowCart(false)}
                    onRemoveItem={handleRemoveFromCart}
                    onUpdateQty={handleUpdateCartQty}
                    onClearCart={handleClearCart}
                />
            )}
        </div>
    );
}